import { Component, JSXElement, For, Show } from 'solid-js';

const SINGLE_BUTTON_THRESHOLD = 2;

/**
 * SolidJS Rightpanel component - Simplified Design
 *
 * Usage Example:
 * ```tsx
 * <Rightpanel
 *   header={
 *     <div>
 *       <h3>My Panel Title</h3>
 *       <button>Action Button</button>
 *     </div>
 *   }
 *   content={<div>Panel content goes here</div>}
 *   footer={{
 *     children: <div>Footer content</div>
 *   }}
 * />
 * ```
 *
 * Note: This component uses `header`, `content`, and `footer` props
 * for JSX content - the host application has full control over the layout.
 */

export interface IRightpanelFooterButtonProps {
  onClick?(event: MouseEvent): void;
  [key: string]: unknown;
}

export interface IRightpanelFooterButton {
  text: string;
  class: string;
  props: IRightpanelFooterButtonProps;
}

export interface IRightpanelFooter {
  button?: IRightpanelFooterButton[];
  children?: JSXElement;
}

export interface RightpanelProps {
  header?: JSXElement; // Simple header content like section
  // Header navigation props
  headerTitle?: string;
  topLeftButton?: string;
  topRightPrimaryButton?: string;
  topRightSecondaryButton?: string;
  footer?: IRightpanelFooter;
  sectionProps?: Record<string, unknown>;
  bodyProps?: Record<string, unknown>;
  content?: JSXElement;
  // Callback props for event handling
  onFooterButtonClick?(
    button: IRightpanelFooterButton,
    event: MouseEvent
  ): void;
  onLeftButtonClick?(): void;
  onPrimaryButtonClick?(): void;
  onSecondaryButtonClick?(): void;
}

const Rightpanel: Component<RightpanelProps> = (props) => {
  const handleFooterButtonClick = (
    button: IRightpanelFooterButton,
    event: MouseEvent
  ): void => {
    // Execute original button props onClick if exists
    if (button.props?.onClick) {
      button.props.onClick(event);
    }

    // Call callback prop if provided
    if (props.onFooterButtonClick) {
      props.onFooterButtonClick(button, event);
    }
  };

  const FooterButtons: Component = () => {
    return (
      <div class="flex gap-2 w-full">
        <For each={props.footer?.button}>
          {(button) => {
            const buttonClass = ['button', button.class];
            if ((props.footer?.button?.length ?? 0) < SINGLE_BUTTON_THRESHOLD) {
              buttonClass.push('full-width', 'mx-0');
            }

            return (
              <button
                class={buttonClass.join(' ')}
                {...button.props}
                onClick={(e) => {
                  handleFooterButtonClick(button, e);
                }}
                textContent={button.text}
              />
            );
          }}
        </For>
      </div>
    );
  };

  const Header: Component = () => {
    return (
      <>
        {props.header ?? (
          <div class="flex flex-row flex-nowrap items-center w-full min-w-0 gap-2">
            <Show when={props.topLeftButton}>
              <button
                type="button"
                class="button icon-medium icon-circle tertiary shrink-0"
                onClick={() => props.onLeftButtonClick?.()}
              >
                <i class={`icon-font ${props.topLeftButton ?? ''}`} />
              </button>
            </Show>
            <Show when={props.headerTitle}>
              <h3 class="header-title m-0 min-w-0 flex-1 truncate">
                {props.headerTitle}
              </h3>
            </Show>
            <div class="ml-auto flex flex-row flex-nowrap items-center gap-2 shrink-0">
              <Show when={props.topRightSecondaryButton}>
                <button
                  type="button"
                  class="button icon-medium icon-circle tertiary"
                  onClick={() => props.onSecondaryButtonClick?.()}
                >
                  <i
                    class={`icon-font ${props.topRightSecondaryButton ?? ''}`}
                  />
                </button>
              </Show>
              <Show when={props.topRightPrimaryButton}>
                <button
                  type="button"
                  class="button icon-medium icon-circle primary"
                  onClick={() => props.onPrimaryButtonClick?.()}
                >
                  <i class={`icon-font ${props.topRightPrimaryButton ?? ''}`} />
                </button>
              </Show>
            </div>
          </div>
        )}
      </>
    );
  };

  return (
    <modus-wc-utility-panel expanded={true}>
      <div slot="header">
        <Header />
      </div>
      <div slot="body" {...props.bodyProps}>
        {props.content}
      </div>
      <Show when={props.footer}>
        <div slot="footer" class={props.footer?.children ? 'p-0' : ''}>
          {props.footer?.children}
          <Show when={props.footer?.button}>
            <div class="px-2 py-2">
              <FooterButtons />
            </div>
          </Show>
        </div>
      </Show>
    </modus-wc-utility-panel>
  );
};

export default Rightpanel;
